import { Box, Card, CardContent, CircularProgress, LinearProgress, Stack } from "@mui/material";
import { useCallback, useMemo, useRef, useState } from "react";

import EmbeddedChat from "@/common/components/Entity/Chat/View/EmbeddedChat";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useAppSelector, useAppThunkDispatch } from "@/common/hooks/redux";
import { useUserProfile } from "@/common/hooks/user/useUserProfile";
import { apiClient } from "@/core/api/ApiClient";
import { ChatHistoryItemType, ChatType } from "@/core/api/generated/v1";
import * as appCommonSlice from "@/store/appCommon/slice";

import AppIconButton from "../../Button/AppIconButton";
import InfoTile from "../../Form/Display/InfoTile";
import AppIcon, { AppIconOf } from "../../Icons/AppIcon";
import AppTypography from "../../Text/AppTypography";
import AppTooltip from "../../Tooltip/AppTooltip";
import { ChatDisplayHandle } from "../Chat/ChatDisplay";

export default function AssistantSideBar() {
  const dispatch = useAppThunkDispatch();
  const profile = useUserProfile();
  const chatRef = useRef<ChatDisplayHandle>(null);

  const isAssistantOpened = useAppSelector((x) => x.app.isAssistantOpened);
  const [isAssistantAnswerLoading, setIsAssistantAnswerLoading] = useState(false);

  const suggestions = useMemo(
    () => [
      {
        title: "Redeem",
        description: "Help me with my NexusOps subscribe",
        iconOf: "subscription" as AppIconOf,
      },
      {
        title: "Get more",
        description: "Show me how to get more options at NexusOps",
        iconOf: "sun" as AppIconOf,
      },
      {
        title: "Recommended",
        description: "Tell me which NexusOps services to use",
        iconOf: "bulb" as AppIconOf,
      },
    ],
    [],
  );

  const threadRequest = useApiRequest(
    apiClient.assistantApi.apiV1AssistantThreadGetOrInitPost,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
    },
    {
      skip: !isAssistantOpened,
      deps: [isAssistantOpened],
    },
  );
  const thread = useMemo(() => threadRequest.data, [threadRequest.data]);

  const startNew = useCallback(async () => {
    if (threadRequest.isLoading) {
      return;
    }
    if (!thread) {
      return;
    }
    // Close current thread
    await apiClient.assistantApi.apiV1AssistantThreadIdDelete({
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      threadId: thread!.id || "",
    });
    // Start new thread
    threadRequest.refetch();
  }, [thread, threadRequest.isLoading]);

  const requestAssistant = useCallback(async () => {
    // request assistant to answer
    try {
      setIsAssistantAnswerLoading(true);
      await apiClient.assistantApi.apiV1AssistantThreadThreadIdCompletionPost({
        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
        threadId: thread!.id || "",
      });
    } finally {
      setIsAssistantAnswerLoading(false);
    }
  }, [thread, isAssistantAnswerLoading]);

  const onClose = useCallback(() => {
    dispatch(appCommonSlice.hideAssistantBlock());
  }, [dispatch, appCommonSlice.hideAssistantBlock]);

  const handleSuggestionClick = useCallback(async (suggestion: string) => {
    console.log("Chat Ref", chatRef.current);
    if (chatRef.current) {
      await chatRef.current?.chat?.message?.input?.sendMessageAsText(suggestion);
    }
  }, []);

  const isLoading = !isAssistantOpened || !thread || threadRequest.isLoading;

  if (!isAssistantOpened) {
    return null;
  }

  return (
    <Box
      data-testid='AssistantSideBar'
      sx={{
        bottom: 50,
        right: 50,
        width: "400px",
        height: "fill-available",
      }}
    >
      {isLoading && <LinearProgress />}
      {!isLoading && (
        <Stack direction='column' sx={{ height: "100%", p: 2 }} spacing={1}>
          {/* Actions */}
          <Stack direction='row' alignItems='center' justifyContent='end' spacing={1}>
            {isAssistantAnswerLoading && <CircularProgress size={24} />}
            {!isAssistantAnswerLoading && (
              <AppTooltip title='Start new chat'>
                <AppIconButton onClick={startNew}>
                  <AppIcon of='refresh' />
                </AppIconButton>
              </AppTooltip>
            )}
            <AppTooltip title='Close'>
              <AppIconButton onClick={onClose}>
                <AppIcon of='close' />
              </AppIconButton>
            </AppTooltip>
          </Stack>

          {/* Chat */}
          <EmbeddedChat
            ref={chatRef}
            chatType={ChatType.Assistant}
            chatId={thread.assistantChat?.chatId}
            chatProps={{
              size: "medium",
              disabled: isAssistantAnswerLoading,
              fullWidth: false,
              fullHeight: true,
              allowPin: false,
              allowClose: false,
              allowLeave: false,
              allowAttachments: false,
              allowOpenInNewTab: false,
              allowEdit: false,
              allowParticipantManagement: false,
              allowAcknowledgement: false,
              allowUpdateTags: false,
              displayProps: {
                actions: false,
              },
              chatHistoryProps: {
                filters: {
                  types: [ChatHistoryItemType.ChatMessage],
                },
                displayProps: {
                  actions: false,
                },
                fallbackContent: (
                  <Card>
                    <CardContent
                      sx={{
                        borderRadius: 0,
                        borderLeft: "5px solid",
                        borderColor: (th) => th.palette.divider,
                      }}
                    >
                      <Stack
                        direction='column'
                        alignItems='start'
                        justifyContent='start'
                        spacing={2}
                      >
                        <AppTypography>Hi, {profile?.personName?.name}</AppTypography>
                        <AppTypography>
                          Ready to explore? Select a suggestion or describe what you want to do.
                        </AppTypography>
                        <Stack spacing={1}>
                          {suggestions.map((item) => (
                            <InfoTile
                              iconOf={item.iconOf as AppIconOf}
                              label={item.title}
                              onClick={() => handleSuggestionClick(item.description)}
                              sx={{ alignItems: "start" }}
                              key={item.title}
                            >
                              {item.description}
                            </InfoTile>
                          ))}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ),
              },
              chatInputFeatures: {
                mentions: false,
                tips: false,
              },
              onSendMessage: requestAssistant,
            }}
          />
        </Stack>
      )}
    </Box>
  );
}
